// ── Survival Games title sequence ──
// Four emblems converge, the seal is struck, the name is forged.

import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CrownSVG, ShieldSVG, BrokenHeartSVG, StarSVG } from './GameIcons';
import { seeded, usePrefersReducedMotion } from '../lib/battle3d';

const TITLE = 'SURVIVAL GAMES';

// Where each emblem starts and where it locks into the ring
const EMBLEMS = [
  { Icon: CrownSVG,       from: { x: -620, y: -420 }, dir: { x: 0,  y: -1 }, tone: '#FBBF24' },
  { Icon: ShieldSVG,      from: { x:  620, y: -420 }, dir: { x: 1,  y: 0 },  tone: '#3B82F6' },
  { Icon: BrokenHeartSVG, from: { x:  620, y:  420 }, dir: { x: 0,  y: 1 },  tone: '#EC4899' },
  { Icon: StarSVG,        from: { x: -620, y:  420 }, dir: { x: -1, y: 0 },  tone: '#A855F7' },
];

// ── Act 1: rising embers in the dark ──
const EmberField = ({ count }) => {
  const embers = useMemo(() => {
    const rnd = seeded(31337);
    return Array.from({ length: count }, () => ({
      x: rnd() * 100, size: 1.5 + rnd() * 3, dur: 4 + rnd() * 6, delay: rnd() * 5, drift: (rnd() - 0.5) * 140,
    }));
  }, [count]);
  return (
    <>
      {embers.map((e, i) => (
        <motion.div key={i}
          initial={{ opacity: 0 }}
          animate={{ y: ['0vh', '-100vh'], x: [0, e.drift], opacity: [0, 0.85, 0.6, 0] }}
          transition={{ duration: e.dur, repeat: Infinity, delay: e.delay, ease: 'linear' }}
          style={{
            position: 'absolute', left: `${e.x}%`, bottom: '-4%',
            width: e.size, height: e.size, borderRadius: '50%',
            background: '#ffb347', boxShadow: `0 0 ${e.size * 4}px #ff8c2e`,
          }}
        />
      ))}
    </>
  );
};

const IntroSequence = ({ onDone }) => {
  const [act, setAct] = useState(0);
  const reduced = usePrefersReducedMotion();
  const wide = typeof window !== 'undefined' && window.innerWidth > 760;
  const RING = wide ? 158 : 104;
  const GLYPH = wide ? 88 : 58;

  useEffect(() => {
    if (reduced) { onDone?.(); return undefined; }
    const t = [
      setTimeout(() => setAct(1), 1500),  // emblems converge
      setTimeout(() => setAct(2), 3600),  // the seal is struck
      setTimeout(() => setAct(3), 4400),  // the name is forged
      setTimeout(() => setAct(4), 6200),  // the promise
    ];
    return () => t.forEach(clearTimeout);
  }, [reduced, onDone]);

  if (reduced) return null;

  return (
    <motion.div
      data-testid="intro-sequence"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, filter: 'blur(14px)', scale: 1.08 }}
      transition={{ duration: 0.75, ease: 'easeInOut' }}
      onClick={() => onDone?.()}
      style={{
        position: 'fixed', inset: 0, zIndex: 400, overflow: 'hidden', cursor: 'pointer',
        background: 'radial-gradient(ellipse at 50% 60%, #14092b 0%, #070315 45%, #000 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        perspective: 1200,
      }}
    >
      <EmberField count={34} />

      {/* Slow breathing aura */}
      <motion.div
        animate={{ opacity: [0.25, 0.55, 0.25], scale: [0.9, 1.1, 0.9] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          position: 'absolute', width: 620, height: 620, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(124,58,237,0.32) 0%, transparent 68%)', filter: 'blur(42px)',
        }}
      />

      {/* ── Act 0: the whisper ── */}
      <AnimatePresence>
        {act === 0 && (
          <motion.div
            key="whisper"
            initial={{ opacity: 0, letterSpacing: '0.9em' }}
            animate={{ opacity: [0, 1, 1, 0], letterSpacing: '0.35em' }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5, times: [0, 0.25, 0.7, 1] }}
            style={{
              position: 'absolute', fontSize: 'clamp(11px,2.4vw,15px)', fontWeight: 700,
              color: 'rgba(233,213,255,0.75)', textTransform: 'uppercase',
              textShadow: '0 0 26px rgba(168,85,247,0.8)',
            }}
          >
            Quattro giochi
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Act 1: the seal ring ── */}
      <AnimatePresence>
        {act === 1 && (
          <motion.svg
            key="ring"
            initial={{ opacity: 0, scale: 0.6, rotate: -40 }}
            animate={{ opacity: 1, scale: 1, rotate: 320 }}
            exit={{ opacity: 0, scale: 1.4 }}
            transition={{ opacity: { duration: 0.8 }, scale: { duration: 1.2 }, rotate: { duration: 9, ease: 'linear' } }}
            width={RING * 2.6} height={RING * 2.6}
            viewBox={`${-RING * 1.3} ${-RING * 1.3} ${RING * 2.6} ${RING * 2.6}`}
            style={{ position: 'absolute', pointerEvents: 'none' }}
          >
            <circle r={RING} fill="none" stroke="rgba(196,181,253,0.30)" strokeWidth="1" strokeDasharray="5 11" />
            <circle r={RING * 0.74} fill="none" stroke="rgba(168,85,247,0.16)" strokeWidth="1" />
            <motion.path
              d={`M0 ${-RING} L${RING} 0 L0 ${RING} L${-RING} 0 Z`}
              fill="none" stroke="rgba(233,213,255,0.4)" strokeWidth="1.4"
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
              transition={{ duration: 1.5, ease: 'easeInOut' }}
            />
          </motion.svg>
        )}
      </AnimatePresence>

      {/* ── Acts 1–2: the emblems converge and are sealed ── */}
      <div style={{ position: 'absolute', transformStyle: 'preserve-3d' }}>
        {EMBLEMS.map((e, i) => {
          const { Icon } = e;
          const converging = act === 1;
          const sealed = act >= 2;
          return (
            <motion.div key={i}
              initial={{ x: e.from.x, y: e.from.y, opacity: 0, rotateY: 0, scale: 0.4 }}
              animate={
                sealed
                  ? { x: 0, y: 0, opacity: 0, scale: 0.25, rotateY: 720 }
                  : converging
                    ? { x: e.dir.x * RING, y: e.dir.y * RING, opacity: 1, scale: 1, rotateY: 360 }
                    : { x: e.from.x, y: e.from.y, opacity: 0, scale: 0.4 }
              }
              transition={{
                duration: sealed ? 0.45 : 1.4,
                delay: sealed ? i * 0.03 : i * 0.09,
                ease: sealed ? 'easeIn' : [0.2, 0.8, 0.2, 1],
              }}
              style={{
                position: 'absolute', left: -GLYPH / 2, top: -GLYPH / 2,
                filter: `drop-shadow(0 0 26px ${e.tone})`,
                transformStyle: 'preserve-3d',
              }}
            >
              <Icon size={GLYPH} />
            </motion.div>
          );
        })}
      </div>

      {/* ── Act 2: the strike ── */}
      {act >= 2 && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0.2, 0] }}
            transition={{ duration: 0.9, times: [0, 0.07, 0.25, 1] }}
            style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 50% 50%, #fff 0%, rgba(216,180,254,0.65) 16%, transparent 52%)' }}
          />
          {[0, 1, 2].map(i => (
            <motion.div key={i}
              initial={{ width: 40, height: 40, opacity: 0.9 }}
              animate={{ width: 900, height: 900, opacity: 0 }}
              transition={{ duration: 1.3, delay: i * 0.14, ease: 'easeOut' }}
              style={{ position: 'absolute', borderRadius: '50%', border: '2px solid rgba(196,181,253,0.7)' }}
            />
          ))}
        </>
      )}

      {/* ── Acts 3–4: the name is forged ── */}
      <AnimatePresence>
        {act >= 3 && (
          <motion.div
            key="title"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ position: 'relative', textAlign: 'center', padding: '0 18px', zIndex: 3 }}
          >
            {/* monogram */}
            <motion.div
              initial={{ scale: 0, rotateY: -180 }}
              animate={{ scale: 1, rotateY: 0 }}
              transition={{ type: 'spring', stiffness: 170, damping: 15 }}
              style={{
                width: 74, height: 74, borderRadius: 20, margin: '0 auto 18px',
                background: 'linear-gradient(135deg,#7c3aed,#ec4899)',
                boxShadow: '0 12px 40px rgba(124,58,237,0.65), inset 0 1px 0 rgba(255,255,255,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 36, fontWeight: 900, color: '#fff', fontFamily: "'Cinzel',serif",
              }}
            >
              S
            </motion.div>

            {/* letters slammed in one by one */}
            <h1 style={{
              margin: 0, display: 'flex', justifyContent: 'center', flexWrap: 'wrap',
              fontFamily: "'Cinzel',serif", fontWeight: 900,
              fontSize: 'clamp(26px,7.5vw,68px)', letterSpacing: '0.1em',
            }}>
              {TITLE.split('').map((ch, i) => (
                <motion.span key={i}
                  initial={{ opacity: 0, y: -40, scale: 2.4, filter: 'blur(10px)' }}
                  animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
                  transition={{ delay: 0.25 + i * 0.045, type: 'spring', stiffness: 260, damping: 18 }}
                  style={{
                    background: 'linear-gradient(to bottom, #ffffff 0%, #e9d5ff 38%, #a78bfa 68%, #6d28d9 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    whiteSpace: 'pre',
                    filter: 'drop-shadow(0 4px 18px rgba(124,58,237,0.6))',
                  }}
                >
                  {ch}
                </motion.span>
              ))}
            </h1>

            {/* shimmer sweeping over the title */}
            <motion.div
              initial={{ x: '-120%' }}
              animate={{ x: '120%' }}
              transition={{ duration: 1.4, delay: 1, ease: 'easeInOut' }}
              style={{
                position: 'absolute', top: 80, left: 0, right: 0, height: 90,
                background: 'linear-gradient(105deg, transparent 42%, rgba(255,255,255,0.35) 50%, transparent 58%)',
                pointerEvents: 'none',
              }}
            />

            <AnimatePresence>
              {act >= 4 && (
                <motion.div key="tag"
                  initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                >
                  <div style={{
                    marginTop: 14, fontSize: 'clamp(12px,2.8vw,16px)', fontWeight: 700,
                    color: 'rgba(233,213,255,0.7)', letterSpacing: '0.3em', textTransform: 'uppercase',
                  }}>
                    Un solo sopravvissuto
                  </div>
                  <motion.button
                    onClick={(e) => { e.stopPropagation(); onDone?.(); }}
                    animate={{ opacity: [0.55, 1, 0.55] }}
                    transition={{ duration: 1.8, repeat: Infinity }}
                    style={{
                      marginTop: 30, padding: '11px 30px', borderRadius: 12,
                      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(196,181,253,0.4)',
                      color: '#e9d5ff', fontSize: 12, fontWeight: 800, letterSpacing: '0.18em',
                      textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Cinzel',serif",
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    Entra
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Skip */}
      {act < 4 && (
        <button
          onClick={(e) => { e.stopPropagation(); onDone?.(); }}
          style={{
            position: 'absolute', bottom: 22, right: 22, zIndex: 5,
            background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(196,181,253,0.25)',
            borderRadius: 9, padding: '6px 14px', color: 'rgba(233,213,255,0.6)',
            fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', cursor: 'pointer',
          }}
        >
          SALTA ›
        </button>
      )}

      {/* Cinematic bars */}
      {[0, 1].map(i => (
        <motion.div key={i}
          initial={{ height: '14vh' }}
          animate={{ height: act >= 3 ? '6vh' : '14vh' }}
          transition={{ duration: 1 }}
          style={{ position: 'absolute', left: 0, right: 0, [i ? 'bottom' : 'top']: 0, background: '#000', zIndex: 4, pointerEvents: 'none' }}
        />
      ))}
    </motion.div>
  );
};

export default IntroSequence;
